import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  CircularProgress, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  FormControlLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { 
  getAllTrackingConfigs, 
  updateGlobalTrackingConfig, 
  updateComponentTrackingConfig,
  deleteTrackingConfig
} from '../../api/trackingConfig';
import { getComponentNames, getInteractionTypes } from '../../api/userInteractionReports';

/**
 * Component for managing tracking configurations
 */
function TrackingConfigPanel() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [configs, setConfigs] = useState(null);
  const [globalEnabled, setGlobalEnabled] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [availableComponents, setAvailableComponents] = useState([]);
  const [availableTypes, setAvailableTypes] = useState([]);
  const [newConfig, setNewConfig] = useState({
    component_name: '',
    event_type: '',
    is_enabled: true
  });

  // Load tracking configurations
  const loadConfigs = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getAllTrackingConfigs();
      setConfigs(data);
      setGlobalEnabled(data.globalConfig.is_enabled);
    } catch (error) {
      console.error('Error loading tracking configurations:', error);
      setError('Failed to load tracking configurations. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Load available components and event types
  const loadOptions = async () => {
    try {
      const [components, types] = await Promise.all([
        getComponentNames(),
        getInteractionTypes()
      ]);
      setAvailableComponents(components);
      setAvailableTypes(types);
    } catch (error) {
      console.error('Error loading options:', error);
    }
  };

  // Load data on mount
  useEffect(() => {
    loadConfigs();
    loadOptions();
  }, []);

  // Handle global tracking toggle
  const handleGlobalToggle = async () => {
    try {
      setSaveError(null);
      const newValue = !globalEnabled;
      await updateGlobalTrackingConfig(newValue);
      setGlobalEnabled(newValue);
    } catch (error) {
      console.error('Error updating global tracking configuration:', error);
      setSaveError('Failed to update global tracking configuration. Please try again later.');
    }
  };

  // Handle component tracking toggle
  const handleComponentToggle = async (config) => {
    try {
      setSaveError(null);
      const newValue = !config.is_enabled;
      await updateComponentTrackingConfig(config.component_name, config.event_type, newValue);
      
      // Update local state
      setConfigs(prevConfigs => ({
        ...prevConfigs,
        componentConfigs: prevConfigs.componentConfigs.map(c => 
          c.id === config.id ? { ...c, is_enabled: newValue } : c
        )
      }));
    } catch (error) {
      console.error('Error updating component tracking configuration:', error);
      setSaveError('Failed to update component tracking configuration. Please try again later.');
    }
  };

  // Handle delete config
  const handleDeleteConfig = async (config) => {
    try {
      setSaveError(null);
      await deleteTrackingConfig(config.id);
      
      // Update local state
      setConfigs(prevConfigs => ({
        ...prevConfigs,
        componentConfigs: prevConfigs.componentConfigs.filter(c => c.id !== config.id)
      }));
    } catch (error) {
      console.error('Error deleting tracking configuration:', error);
      setSaveError('Failed to delete tracking configuration. Please try again later.');
    }
  };

  // Handle add dialog open
  const handleAddDialogOpen = () => {
    setOpenAddDialog(true);
  };

  // Handle add dialog close
  const handleAddDialogClose = () => {
    setOpenAddDialog(false);
    setNewConfig({
      component_name: '',
      event_type: '',
      is_enabled: true
    });
  };

  // Handle new config change
  const handleNewConfigChange = (field, value) => {
    setNewConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle add config
  const handleAddConfig = async () => {
    try {
      setSaveError(null);
      
      // Validate
      if (!newConfig.component_name || !newConfig.event_type) {
        setSaveError('Component and event type are required.');
        return;
      }
      
      // Check if config already exists
      const exists = configs.componentConfigs.some(
        c => c.component_name === newConfig.component_name && c.event_type === newConfig.event_type
      );
      
      if (exists) {
        setSaveError('This configuration already exists.');
        return;
      }
      
      // Save
      await updateComponentTrackingConfig(
        newConfig.component_name,
        newConfig.event_type,
        newConfig.is_enabled
      );
      
      // Reload configs
      await loadConfigs();
      
      // Close dialog
      handleAddDialogClose();
    } catch (error) {
      console.error('Error adding tracking configuration:', error);
      setSaveError('Failed to add tracking configuration. Please try again later.');
    }
  };

  // If loading, show loading indicator
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  // If error, show error message
  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Tracking Configuration
      </Typography>
      
      {saveError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {saveError}
        </Alert>
      )}
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Global Tracking
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={globalEnabled}
                onChange={handleGlobalToggle}
                color="primary"
              />
            }
            label={globalEnabled ? 'Enabled' : 'Disabled'}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          When global tracking is disabled, no interactions will be tracked regardless of component settings.
        </Typography>
      </Paper>
      
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Component Tracking
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddDialogOpen}
          >
            Add Configuration
          </Button>
        </Box>
        
        <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="tracking configuration table">
            <TableHead>
              <TableRow>
                <TableCell>Component</TableCell>
                <TableCell>Event Type</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.componentConfigs.map((config) => (
                <TableRow
                  key={config.id}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {config.component_name}
                  </TableCell>
                  <TableCell>{config.event_type}</TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={config.is_enabled}
                      onChange={() => handleComponentToggle(config)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<DeleteIcon />}
                      onClick={() => handleDeleteConfig(config)}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {configs.componentConfigs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No component tracking configurations found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      
      {/* Add Configuration Dialog */}
      <Dialog open={openAddDialog} onClose={handleAddDialogClose}>
        <DialogTitle>Add Tracking Configuration</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Select a component and event type to configure tracking.
          </DialogContentText>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 400 }}>
            <FormControl fullWidth>
              <InputLabel id="component-label">Component</InputLabel>
              <Select
                labelId="component-label"
                id="component-select"
                value={newConfig.component_name}
                label="Component"
                onChange={(e) => handleNewConfigChange('component_name', e.target.value)}
              >
                {availableComponents.map((component) => (
                  <MenuItem key={component} value={component}>
                    {component}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel id="event-type-label">Event Type</InputLabel>
              <Select
                labelId="event-type-label"
                id="event-type-select"
                value={newConfig.event_type}
                label="Event Type"
                onChange={(e) => handleNewConfigChange('event_type', e.target.value)}
              >
                {availableTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControlLabel
              control={
                <Switch
                  checked={newConfig.is_enabled}
                  onChange={(e) => handleNewConfigChange('is_enabled', e.target.checked)}
                  color="primary"
                />
              }
              label={newConfig.is_enabled ? 'Enabled' : 'Disabled'}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddDialogClose}>Cancel</Button>
          <Button onClick={handleAddConfig} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TrackingConfigPanel;
